import React, { useState, useEffect, useRef } from "react";
import axios from "axios";

import { translate } from "../../../hooks/translate";
import { useAuth } from "../../../services/useAuth";

import AppPage from "../../../components/CustomUI/AppPage";
import CardContainer from "../../../components/CustomUI/CardContainer";
import Container from "../../../components/CustomUI/Container";
import ContainerItem from "../../../components/CustomUI/Container/ContainerItem";
import Avatar from "../../../components/CustomUI/Avatar";
import Lozenge from "../../../components/BaseUI/Lozenge";
import { Accordion, AccordionItem } from "../../../components/BaseUI/Accordion";
import RichTextEditor from "../../../components/CustomUI/RichTextEditor";
import Input from "../../../components/BaseUI/Input";
import { ToastContainer, Toast } from "../../../components/CustomUI/Toast";
import Button from "../../../components/BaseUI/Button";
import NotificationPreferences from "../../../components/CustomUI/NotificationPreferences";

import "./Profile.scss";

const API_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:3100";

export default function Profile() {
  const { getToken, user: authUser } = useAuth();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toasts, setToasts] = useState([]);
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const fetchedRef = useRef(false);

  const accordions = [
    // { title: translate("PROFILE.ABOUT_YOU") },
    { title: translate("PROFILE.PERSONAL_INFORMATION") },
    { title: translate("PROFILE.CHANGE_PASSWORD") },
    { title: "Bildirim Tercihleri" },
    // { title: translate("PROFILE.LANGUAGE_AND_LOCATION") },
  ];

  useEffect(() => {
    if (authUser && !fetchedRef.current) {
      setUserData(authUser);
      fetchedRef.current = true;
      setLoading(false);
    } else if (!authUser && !fetchedRef.current) {
      fetchUserData();
    }
  }, [authUser]);

  const fetchUserData = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });

      if (response.data.success) {
        setUserData(response.data.user);
        fetchedRef.current = true;
      }
    } catch (error) {
      console.error("Kullanıcı bilgileri getirme hatası:", error);
      setToasts([
        {
          description: "Kullanıcı bilgileri yüklenirken bir hata oluştu.",
          id: Date.now(),
          title: "Hata!",
          appearance: "danger",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = (name, value) => {
    // console.log(e)
    // if (!e || !e.target) return;

    setPasswordData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handlePasswordSubmit = async () => {
    try {
      // Validate passwords
      if (passwordData.newPassword !== passwordData.confirmPassword) {
        setToasts([
          {
            description: "Yeni şifreler eşleşmiyor.",
            id: Date.now(),
            title: "Hata!",
            appearance: "danger",
          },
        ]);
        return;
      }

      if (passwordData.newPassword.length < 6) {
        setToasts([
          {
            description: "Yeni şifre en az 6 karakter olmalıdır.",
            id: Date.now(),
            title: "Hata!",
            appearance: "danger",
          },
        ]);
        return;
      }

      setIsChangingPassword(true);
      const response = await axios.put(
        `${API_URL}/api/users/${userData._id}`,
        {
          password: passwordData.newPassword,
          updatePassword: true, // Make sure this flag matches the backend logic
        },
        {
          headers: {
            Authorization: `Bearer ${getToken()}`, // Ensure the token is retrieved correctly
          },
        },
      );

      if (response.data.success) {
        setToasts([
          {
            description: "Şifreniz başarıyla değiştirildi.",
            id: Date.now(),
            title: "Başarılı!",
            appearance: "success",
          },
        ]);
        // Clear password fields
        setPasswordData({
          currentPassword: "",
          newPassword: "",
          confirmPassword: "",
        });
      }
    } catch (error) {
      console.error("Şifre değiştirme hatası:", error);
      setToasts([
        {
          description:
            error.response?.data?.message ||
            "Şifre değiştirilirken bir hata oluştu.",
          id: Date.now(),
          title: "Hata!",
          appearance: "danger",
        },
      ]);
    } finally {
      setIsChangingPassword(false);
    }
  };

  const removeToast = (index) => {
    const values = toasts;
    values.splice(index, 1);
    setToasts([...values]);
  };

  if (loading) {
    return <div>Yükleniyor...</div>;
  }

  return (
    <AppPage
      title={translate("PROFILE.TITLE")}
      breadcrumbs={[
        { label: "Rovion", url: "" },
        { label: translate("PROFILE.TITLE") },
      ]}
      className="profile-container"
    >
      <CardContainer>
        <div className="profile-container__header">
          <div className="profile-container__header--avatar">
            <Avatar name={userData?.name} size="xxlarge" />
          </div>
        </div>

        <div className="profile-container__user">
          <h2>{userData?.name}</h2>

          <div className="d-flex align-items-center gap-1">
            <span>{userData?.role}</span>
            {userData?.company && (
              <>
                |
                <Lozenge appearance="default-subtle">
                  {userData.company.name}
                </Lozenge>
              </>
            )}
          </div>
        </div>

        <div className="p-3">
          <Accordion items={accordions} gap="8px">
            {/* <AccordionItem>
              <RichTextEditor
                content={`<p>Merhaba! Ben <strong>${userData?.name}</strong>. ${
                  userData?.company
                    ? `<strong>${userData.company.name}</strong> firmasında`
                    : ""
                } <em>${userData?.role}</em> olarak görev yapıyorum.</p>`}
                readOnly
              />
            </AccordionItem> */}

            <AccordionItem>
              <Container>
                <ContainerItem sm={4} md={4} xl={6}>
                  <Input label="E-mail" value={userData?.email} disabled />
                </ContainerItem>

                {userData?.company && (
                  <ContainerItem sm={4} md={4} xl={6}>
                    <Input
                      label={translate("PROFILE.DEPARTMENT")}
                      value={userData.role}
                      disabled
                    />
                  </ContainerItem>
                )}

                <ContainerItem sm={4} md={4} xl={6}>
                  <Input
                    label={translate("PROFILE.LAST_LOGIN")}
                    value={new Date(userData?.lastLogin).toLocaleString(
                      "tr-TR",
                    )}
                    disabled
                  />
                </ContainerItem>
              </Container>
            </AccordionItem>

            <AccordionItem>
              <Container>
                <ContainerItem sm={4} md={4} xl={6}>
                  <Input
                    label={translate("PROFILE.CURRENT_PASSWORD")}
                    type="password"
                    value={passwordData.currentPassword}
                    onChange={(value) =>
                      handlePasswordChange("currentPassword", value)
                    }
                    placeholder="Mevcut şifrenizi girin"
                    required
                    valid={passwordData.currentPassword ? true : undefined}
                    message={
                      !passwordData.currentPassword
                        ? "Mevcut şifre gereklidir"
                        : ""
                    }
                  />
                </ContainerItem>

                <ContainerItem sm={4} md={4} xl={6}>
                  <Input
                    label={translate("PROFILE.NEW_PASSWORD")}
                    type="password"
                    value={passwordData.newPassword}
                    onChange={(value) =>
                      handlePasswordChange("newPassword", value)
                    }
                    placeholder="Yeni şifrenizi girin"
                    required
                    valid={
                      passwordData.newPassword &&
                      passwordData.newPassword.length >= 6
                        ? true
                        : undefined
                    }
                    message={
                      !passwordData.newPassword
                        ? "Yeni şifre gereklidir"
                        : passwordData.newPassword.length < 6
                          ? "Yeni şifre en az 6 karakter olmalıdır"
                          : ""
                    }
                  />
                </ContainerItem>

                <ContainerItem sm={4} md={4} xl={6}>
                  <Input
                    label={translate("PROFILE.CONFIRM_PASSWORD")}
                    type="password"
                    value={passwordData.confirmPassword}
                    onChange={(value) =>
                      handlePasswordChange("confirmPassword", value)
                    }
                    placeholder="Yeni şifrenizi tekrar girin"
                    required
                    valid={
                      passwordData.confirmPassword &&
                      passwordData.confirmPassword === passwordData.newPassword
                        ? true
                        : undefined
                    }
                    message={
                      !passwordData.confirmPassword
                        ? "Şifre tekrarı gereklidir"
                        : passwordData.confirmPassword !==
                            passwordData.newPassword
                          ? "Şifreler eşleşmiyor"
                          : ""
                    }
                  />
                </ContainerItem>

                <ContainerItem sm={4} md={4} xl={6}>
                  <Button
                    appearance="primary-animated"
                    onClick={handlePasswordSubmit}
                    disabled={
                      isChangingPassword ||
                      !passwordData.currentPassword ||
                      !passwordData.newPassword ||
                      !passwordData.confirmPassword ||
                      passwordData.newPassword.length < 6 ||
                      passwordData.newPassword !== passwordData.confirmPassword
                    }
                    className="mt-3"
                  >
                    {isChangingPassword
                      ? "Değiştiriliyor..."
                      : "Şifreyi Değiştir"}
                  </Button>
                </ContainerItem>
              </Container>
            </AccordionItem>

            <AccordionItem>
              <NotificationPreferences />
            </AccordionItem>

            {/* <AccordionItem>
              <Container>
                <ContainerItem sm={4} md={4} xl={6}>
                  <Input
                    label={translate("PROFILE.LANGUAGE")}
                    value={
                      userData?.settings?.language === "tr"
                        ? "Türkçe"
                        : "English"
                    }
                    disabled
                  />
                </ContainerItem>

                <ContainerItem sm={4} md={4} xl={6}>
                  <Input
                    label="Theme"
                    value={
                      userData?.settings?.theme === "dark" ? "Dark" : "Light"
                    }
                    disabled
                  />
                </ContainerItem>
              </Container>
            </AccordionItem> */}
          </Accordion>
        </div>
      </CardContainer>

      <ToastContainer>
        {toasts?.map((toast, index) => (
          <Toast
            {...toast}
            key={index}
            // actions={[{ content: "Kapat", onClick: () => removeToast(index) }]}
            onDismissed={() => removeToast(index)}
            onClose={() => removeToast(index)}
          />
        ))}
      </ToastContainer>
    </AppPage>
  );
}
